// Eval-set regression runs (C3.1) — view the scored runs for an eval set via the admin-worker
// (super_tenant_admin). Enter an eval_set id to see each run's model, pass rate, and composite. PLAN-C3.1.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type RunRow = Record<string, unknown>;

export function EvalRunsRoute() {
  const [evalSetId, setEvalSetId] = useState("");
  const [submitted, setSubmitted] = useState("");

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["admin", "eval-runs", submitted],
    queryFn: () => api.listEvalRuns(submitted) as Promise<RunRow[]>,
    enabled: submitted.length > 0,
  });
  const runs = data ?? [];

  const columns: Column<RunRow>[] = [
    { key: "modelId", header: "Model", render: (r) => String(r.modelId ?? "—") },
    {
      key: "status", header: "Status",
      render: (r) => (
        <span className={cn(r.status === "completed" ? "text-emerald-700" : r.status === "failed" ? "text-destructive" : "text-foreground")}>
          {humanize(r.status)}
        </span>
      ),
    },
    {
      key: "passed", header: "Passed / total",
      render: (r) => {
        const total = Number(r.casesTotal ?? 0), passed = Number(r.casesPassed ?? 0);
        return <span className={cn(total > 0 && passed === total ? "text-emerald-700" : "text-amber-700")}>{passed} / {total}</span>;
      },
    },
    { key: "compositeScore", header: "Composite", align: "right", render: (r) => (r.compositeScore != null ? Number(r.compositeScore).toFixed(3) : "—") },
    { key: "startedAt", header: "Started", render: (r) => (r.startedAt ? String(r.startedAt).slice(0, 16).replace("T", " ") : "—") },
  ];

  return (
    <section>
      <PageHeader
        title="Eval-set runs"
        description="Regression runs of an eval set against a pinned prompt + model — pass rate + composite score."
        actions={isFetching ? <span className="text-sm text-muted-foreground">updating…</span> : undefined}
      />

      <form onSubmit={(e) => { e.preventDefault(); setSubmitted(evalSetId.trim()); }} className="mb-4 flex flex-wrap items-center gap-2">
        <Input value={evalSetId} onChange={(e) => setEvalSetId(e.target.value)} placeholder="eval_set id (uuid)" className="w-[360px] max-w-full" />
        <Button type="submit" disabled={!evalSetId.trim()}>Load runs</Button>
      </form>

      {!submitted && <p className="text-sm text-muted-foreground">Enter an eval_set id to load its runs.</p>}
      {error && <p className="text-sm text-destructive">Failed: {(error as Error).message}</p>}

      {submitted && !error && (
        <DataTable
          columns={columns}
          rows={runs}
          loading={isLoading}
          getRowId={(r) => String(r.id ?? Math.random())}
          empty="No runs for this eval set yet."
        />
      )}
    </section>
  );
}
